/* eslint-disable max-lines */
/* eslint-disable react/jsx-no-bind */
import type { TSeverityBadgeVariant, TTagVariant } from "@fluidattacks/design";
import {
  Container,
  Icon,
  IconButton,
  Link,
  SeverityBadge,
  Tag,
  Text,
  Tooltip,
} from "@fluidattacks/design";
import type { CellContext } from "@tanstack/react-table";
import dayjs, { extend } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import _ from "lodash";
import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { getTagVariant } from "./colors";
import { msgError, msgSuccess } from "./notifications";

import type { IVulnerabilityAttr } from "features/fix-priority/types";
import type { IPhoneAttr } from "features/verify-dialog/types";
import type {
  IVulnRowAttr,
  IVulnerabilityAdvisories,
} from "features/vulnerabilities/types";
import { FindingLastReportInterval } from "gql/graphql";
import type { HookEventType } from "gql/graphql";
import { HOOK_EVENT_TO_LABEL } from "pages/integrations/webhooks/add-hook-modal/types";
import { translate } from "utils/translations/translate";

const castEventType = (field: string): string => {
  const eventType: Record<string, string> = {
    AUTHORIZATION_SPECIAL_ATTACK:
      "group.events.type.authorizationSpecialAttack",
    CLIENT_CANCELS_PROJECT_MILESTONE:
      "group.events.type.clientCancelsProjectMilestone",
    CLIENT_EXPLICITLY_SUSPENDS_PROJECT:
      "group.events.type.clientSuspendsProject",
    CLONING_ISSUES: "group.events.type.cloningIssues",
    CREDENTIAL_ISSUES: "group.events.type.credentialsIssues",
    DATA_UPDATE_REQUIRED: "group.events.type.dataUpdateRequired",
    ENVIRONMENT_ISSUES: "group.events.type.environmentIssues",
    INSTALLER_ISSUES: "group.events.type.installerIssues",
    MISSING_SUPPLIES: "group.events.type.missingSupplies",
    MOVED_TO_ANOTHER_GROUP: "group.events.type.movedToAnotherGroup",
    NETWORK_ACCESS_ISSUES: "group.events.type.networkAccessIssues",
    OTHER: "group.events.type.other",
    REMOTE_ACCESS_ISSUES: "group.events.type.remoteAccessIssues",
    TOE_DIFFERS_APPROVED: "group.events.type.toeDiffersApproved",
    VPN_ISSUES: "group.events.type.vpnIssues",
  };

  return eventType[field];
};

const castEventStatus = (
  field: string,
): {
  status: string;
  tagVariant: TTagVariant;
} => {
  const eventStatus: Record<
    string,
    {
      status: string;
      tagVariant: TTagVariant;
    }
  > = {
    CREATED: {
      status: "searchFindings.tabEvents.statusValues.unsolved",
      tagVariant: "error",
    },
    OPEN: {
      status: "searchFindings.tabEvents.statusValues.unsolved",
      tagVariant: "error",
    },
    SOLVED: {
      status: "searchFindings.tabEvents.statusValues.solve",
      tagVariant: "success",
    },
    VERIFICATION_REQUESTED: {
      status: "searchFindings.tabEvents.statusValues.pendingVerification",
      tagVariant: "warning",
    },
  };

  return eventStatus[field];
};

const formatDropdownField = (field: string): string => {
  const translationParameters: Record<string, string> = {
    ACCEPTED: "searchFindings.tabDescription.treatment.accepted",
    ACCEPTED_UNDEFINED:
      "searchFindings.tabDescription.treatment.acceptedUndefined",
    APPLICATIONS: "searchFindings.tabDescription.ambit.applications",
    CLOSED: "searchFindings.tabDescription.treatment.closed",
    DATABASES: "searchFindings.tabDescription.ambit.databases",
    INFRASTRUCTURE: "searchFindings.tabDescription.ambit.infra",
    IN_PROGRESS: "searchFindings.tabDescription.treatment.inProgress",
    NEW: "searchFindings.tabDescription.treatment.new",
    REJECTED: "searchFindings.tabDescription.treatment.rejected",
    SOURCE_CODE: "searchFindings.tabDescription.ambit.sourcecode",
    UNTREATED: "searchFindings.tabDescription.treatment.new",
  };

  return translationParameters[field];
};

const formatVulnerabilityState = (status: string): string => {
  return (
    {
      DELETED: "Deleted",
      MASKED: "Masked",
      REJECTED: translate.t("searchFindings.tabVuln.rejected"),
      SAFE: translate.t("searchFindings.tabVuln.safe"),
      SUBMITTED: translate.t("searchFindings.tabVuln.submitted"),
      VULNERABLE: translate.t("searchFindings.tabVuln.open"),
    }[status] ?? status
  );
};

const formatReattack = (reattack: string): string => {
  return (
    {
      NotRequested: translate.t("searchFindings.tabVuln.notRequested"),
      // eslint-disable-next-line camelcase
      On_hold: translate.t("searchFindings.tabVuln.onHold"),
      Requested: translate.t("searchFindings.tabVuln.requested"),
      Verified: translate.t("searchFindings.tabVuln.verified"),
    }[reattack] ?? reattack
  );
};

const formatTechnique = (technique: string): string => {
  return translate.t(
    `searchFindings.tabVuln.technique.${technique.toLowerCase()}`,
  );
};

const formatTreatment = (treatment: string): string => {
  return translate.t(formatDropdownField(treatment));
};

const formatLastReportInterval = (
  lastReportInterval: FindingLastReportInterval,
): string => {
  switch (lastReportInterval) {
    case FindingLastReportInterval.Last_7:
      return "Last 7 days";
    case FindingLastReportInterval.Last_30:
      return "Last 30 days";
    case FindingLastReportInterval.Last_90:
      return "Last 90 days";
    case FindingLastReportInterval.LastYear:
      return "Last year";
    default:
      return "Last 30 days";
  }
};

const formatPhone = (value: string): IPhoneAttr => {
  const [dialCode, ...phone] = value.split(" ");

  return {
    callingCountryCode: dialCode.replace("+", ""),
    // eslint-disable-next-line require-unicode-regexp
    nationalNumber: phone.join("").replaceAll(/[^0-9]/g, ""),
  };
};

const formatPercentage = (value: number, decimal = false): string => {
  const oneHundred = 100;
  const minDecimal = 0.001;
  const [before, after = "00"] = (value * oneHundred).toString().split(".");

  if (decimal)
    return value < minDecimal && value > 0
      ? "0.001%"
      : `${before}.${after.slice(0, 2)}%`;

  const percentageValue = Math.floor(value * oneHundred);

  return `${percentageValue}%`;
};

const includeTagsFormatter = ({
  dataPrivate = false,
  text,
  customTag,
  excludeTag = false,
  failedTag = false,
  newTag = false,
  reviewTag = false,
}: {
  dataPrivate?: boolean;
  text: JSX.Element | string | undefined;
  customTag?: JSX.Element;
  excludeTag?: boolean;
  newTag?: boolean;
  reviewTag?: boolean;
  failedTag?: boolean;
  decor?: string;
}): JSX.Element => {
  return (
    <Container
      {...(dataPrivate && { "data-private": true })}
      display={"inline-block"}
    >
      <Container alignItems={"center"} display={"flex"} gap={0.25}>
        {customTag}
        {text === undefined ? undefined : (
          <Text color={"#212a36"} display={"inline-block"} size={"md"}>
            {text}
          </Text>
        )}
        {failedTag ? (
          <Tag
            priority={"low"}
            tagLabel={translate.t("formatters.includeTags.failed")}
            variant={"error"}
          />
        ) : undefined}
        {newTag ? (
          <Tag
            priority={"medium"}
            tagLabel={translate.t("formatters.includeTags.new")}
            variant={"error"}
          />
        ) : undefined}
        {reviewTag ? (
          <Tag
            priority={"medium"}
            tagLabel={translate.t("formatters.includeTags.review")}
            variant={"warning"}
          />
        ) : undefined}
        {excludeTag ? (
          <Tag
            icon={"ban"}
            priority={"low"}
            tagLabel={translate.t("formatters.includeTags.exclude")}
            variant={"warning"}
          />
        ) : undefined}
      </Container>
    </Container>
  );
};

const listTagsFormatter = (list: HookEventType[]): JSX.Element => {
  return (
    <Fragment>
      {list.map(
        (el): JSX.Element => (
          <Tag key={el} tagLabel={HOOK_EVENT_TO_LABEL[el]} />
        ),
      )}
    </Fragment>
  );
};

const getVariant = (severity: number): TSeverityBadgeVariant => {
  const midSeverity = 7;
  const maxSeverity = 9;
  if (severity === 0) {
    return "disable";
  }

  if (severity > 0 && severity < 4) {
    return "low";
  } else if (severity >= 4 && severity < midSeverity) {
    return "medium";
  } else if (severity >= midSeverity && severity < maxSeverity) {
    return "high";
  }

  return "critical";
};

const severityFormatter = (severity?: number): JSX.Element => {
  const score = severity === undefined ? "0.0" : severity.toFixed(1);
  const variant = getVariant(severity ?? 0);
  const capitalizedVariant =
    variant === "disable"
      ? "None"
      : variant.charAt(0).toUpperCase() + variant.slice(1);

  return (
    <SeverityBadge textL={score} textR={capitalizedVariant} variant={variant} />
  );
};

const tagsFormatter = (tags: unknown): JSX.Element => {
  if (typeof tags !== "string") return <div>{"-"}</div>;

  const tagsArray = [...new Set(tags.split(",").filter(Boolean))];

  return (
    <Container display={"flex"} gap={0.25}>
      {tagsArray.map(
        (tag): JSX.Element => (
          <Tag key={tag} tagLabel={tag} />
        ),
      )}
    </Container>
  );
};

const formatBoolean = (value: boolean): string =>
  value ? translate.t("yes") : translate.t("no");

const formatStatus = (vulnerable: boolean): string =>
  vulnerable ? translate.t("status.vulnerable") : translate.t("status.safe");

const jsxStatus = (status: string): JSX.Element => {
  const variant = getTagVariant(status);

  return (
    <Tag
      priority={variant === "inactive" ? "default" : "low"}
      tagLabel={status}
      variant={variant}
    />
  );
};

const onlineStatusBadge = (isOnline: boolean): JSX.Element =>
  isOnline ? (
    <Tag
      priority={"low"}
      tagLabel={translate.t("components.sideBar.help.chat.status.online")}
      variant={"success"}
    />
  ) : (
    <Tag
      tagLabel={translate.t("components.sideBar.help.chat.status.offline")}
      variant={"inactive"}
    />
  );

const formatLinkHandler = (
  link: string,
  text: string,
  showInfo = false,
  tip?: string,
  showError = false,
  onClick?: () => void,
): JSX.Element => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (onClick) {
      onClick();
    }
    event.stopPropagation();
  };

  const linkComponent = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div onClick={handleClick} role={"button"} tabIndex={0}>
      <Link href={link}>{_.capitalize(text)}</Link>
    </div>
  );
  if (showInfo || showError) {
    return (
      <Container
        display={"flex"}
        flexDirection={showInfo ? "row" : "row-reverse"}
      >
        {linkComponent}
        &nbsp;
        <Tooltip
          display={"inline"}
          id={`${_.camelCase(text)}Tooltip`}
          place={"top"}
          tip={tip}
        >
          {showInfo ? (
            <sup>
              <Icon
                icon={"info-circle"}
                iconColor={"#5c5c70"}
                iconSize={"lg"}
              />
            </sup>
          ) : (
            <Icon
              icon={"circle-exclamation"}
              iconColor={"#f04438"}
              iconSize={"lg"}
              iconType={"fa-light"}
            />
          )}
        </Tooltip>
      </Container>
    );
  }

  return linkComponent;
};

const timeFromNow = (value: string): string => {
  if (!value) return "-";

  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";

  extend(relativeTime);

  return dayjs(value, "YYYY-MM-DD hh:mm:ss A").fromNow();
};

function createLocationFormatter<
  T extends {
    where: string;
    id: string;
    advisories?: IVulnerabilityAdvisories | null;
  },
>(): (cell: CellContext<T, unknown>) => JSX.Element | string {
  return function LocationFormatter(
    cell: CellContext<T, unknown>,
  ): JSX.Element | string {
    const { t } = useTranslation();
    const vuln = cell.row.original;
    const text = vuln.advisories
      ? `${vuln.where} (${vuln.advisories.cve[0]}, ${vuln.advisories.package}, ${vuln.advisories.vulnerableVersion})`
      : vuln.where;

    const handleCopy = useCallback(
      async (ev: React.SyntheticEvent): Promise<void> => {
        ev.stopPropagation();
        const { clipboard } = navigator;
        const currentUrl = `${window.location.href}/${vuln.id}`;
        if (_.isUndefined(clipboard)) {
          msgError(t("searchFindings.copyUrl.failed"));
        } else {
          await clipboard.writeText(currentUrl);
          msgSuccess(
            t("searchFindings.copyUrl.success"),
            t("searchFindings.copyUrl.successTitle"),
          );
        }
      },
      [t, vuln.id],
    );

    return (
      <Container
        {...{ "data-private": true }}
        display={"inline-block"}
        width={"100%"}
      >
        <Container
          alignItems={"center"}
          display={"flex"}
          gap={0.25}
          justify={"space-between"}
          width={"100%"}
        >
          <Text color={"#212a36"} display={"inline-block"} size={"md"}>
            {text}
          </Text>
          <IconButton
            icon={"copy"}
            iconSize={"sm"}
            iconType={"fa-light"}
            onClick={handleCopy}
            tooltip={t("searchFindings.copyUrl.tooltip")}
            variant={"ghost"}
          />
        </Container>
      </Container>
    );
  };
}

const LocationFormatter = createLocationFormatter<IVulnRowAttr>();
const LocationPriorityFormatter = createLocationFormatter<IVulnerabilityAttr>();

export {
  castEventType,
  castEventStatus,
  formatBoolean,
  formatDropdownField,
  formatPercentage,
  formatPhone,
  formatStatus,
  formatReattack,
  formatTechnique,
  formatLastReportInterval,
  formatTreatment,
  formatVulnerabilityState,
  jsxStatus,
  LocationPriorityFormatter,
  includeTagsFormatter,
  listTagsFormatter,
  LocationFormatter,
  onlineStatusBadge,
  severityFormatter,
  tagsFormatter,
  formatLinkHandler,
  timeFromNow,
};
